import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Session, SessionReflections } from 'types/Session';

import { Button } from 'components/_new/Button';
import { MessageBox, MessageBoxRef } from 'components/_new/MessageBox';
import { InputTextArea } from 'components/_new/InputTextArea';
import { Icon } from 'components/_new/Icon';
import { Box, BoxSection } from 'components/_new/Box';
import { ViewType, ViewTypeDropdown } from 'components/_new/ViewTypeDropdown';
import { LinkDocArticleWithIconWrapper } from '../components/LinkDocArticleWithIconWrapper';

type ReflectionType =
	| 'general'
	| 'thingsEasy'
	| 'techniques'
	| 'barriers'
	| 'thingsHard'
	| 'selfReflection'
	| 'happyFor'
	| 'sadFor'
	| 'nextSteps'
	| 'feedback'
	| 'other'
	| 'result';

type ReflectionSubpageProps = {
	session: Session;
	reflections: Array<{
		key: string;
		title: string;
		value: string | null;
	}>;
	onSave: (values: Partial<SessionReflections>, showToast?: boolean) => void;
	loading: boolean;
};

type ReflectionForm = {
	[key in ReflectionType]: string;
};

export const ReflectionSubpage = ({ reflections, onSave, loading }: ReflectionSubpageProps) => {
	const { t } = useTranslation();
	const hasProfessionalAnswers = useMemo(() => {
		const ignoredKeys: ReflectionType[] = ['general', 'happyFor', 'sadFor'];
		return reflections.some(
			({ key, value }) => !ignoredKeys.includes(key as ReflectionType) && String(value || '').trim().length > 0
		);
	}, [reflections]);
	const [viewType, setViewType] = useState<ViewType>(hasProfessionalAnswers ? 'professionalView' : 'simpleView');

	const autosaveMessageRefs = useRef<Record<string, MessageBoxRef>>({});

	const defaultValues: ReflectionForm = useMemo(
		() => Object.fromEntries(reflections.map(({ key, value }) => [key, value || ''])) as ReflectionForm,
		[reflections]
	);

	const { control, handleSubmit, reset, getValues } = useForm<ReflectionForm>({ defaultValues });

	useEffect(() => {
		reset(defaultValues);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [defaultValues]);

	useEffect(() => {
		if (hasProfessionalAnswers) {
			setViewType('professionalView');
		}
	}, [hasProfessionalAnswers]);

	const handleSubmitForm = handleSubmit((values) => {
		onSave(values, true);
	});

	const autosave = (reflectionKey: ReflectionType) => {
		const value = getValues(reflectionKey);
		if (value !== defaultValues[reflectionKey]) {
			onSave(
				{
					[reflectionKey]: value,
				},
				false
			);
			autosaveMessageRefs.current[reflectionKey]?.show();
		}
	};

	const simpleViewMap = useMemo(() => {
		const map = new Map<'general' | 'happyFor' | 'sadFor', string>();
		map.set('general', t('userPanel.sessionsNew.reflection.introText.secondaryList.el1'));
		map.set('happyFor', t('userPanel.sessionsNew.reflection.introText.secondaryList.el2'));
		map.set('sadFor', t('userPanel.sessionsNew.reflection.introText.secondaryList.el3'));
		return map;
	}, []);

	const sections = useMemo(() => {
		const menteeGrouping: Array<{
			title: string;
			keys: ReflectionType[];
		}> = [
			{ title: t('userPanel.sessionsNew.reflection.sections.reaction'), keys: ['general'] },
			{
				title: t('userPanel.sessionsNew.reflection.sections.reflection'),
				keys: ['thingsEasy', 'happyFor', 'thingsHard', 'feedback'],
			},
			{ title: t('userPanel.sessionsNew.reflection.sections.result'), keys: ['result'] },
			{ title: t('userPanel.sessionsNew.reflection.sections.action'), keys: ['nextSteps', 'other'] },
			{ title: t('userPanel.sessionsNew.reflection.sections.techniques'), keys: ['techniques'] },
		];

		const mentorGrouping: Array<{
			title: string;
			keys: ReflectionType[];
		}> = [
			{ title: t('userPanel.sessionsNew.reflection.sections.reaction'), keys: ['general'] },
			{
				title: t('userPanel.sessionsNew.reflection.sections.reflection'),
				keys: ['barriers', 'thingsHard', 'selfReflection', 'happyFor', 'sadFor'],
			},
			{ title: t('userPanel.sessionsNew.reflection.sections.result'), keys: ['result'] },
			{ title: t('userPanel.sessionsNew.reflection.sections.action'), keys: ['other'] },
			{ title: t('userPanel.sessionsNew.reflection.sections.techniques'), keys: ['techniques'] },
		];

		const isMentee = reflections.some((r) => r.key === 'thingsEasy');
		const grouping = isMentee ? menteeGrouping : mentorGrouping;

		return grouping
			.map((section) => ({
				...section,
				items: reflections.filter((r) => section.keys.includes(r.key as any)),
			}))
			.filter((section) => section.items.length > 0);
	}, [reflections, t]);

	return (
		<form className="flex flex-column gap-2" onSubmit={handleSubmitForm}>
			<h2 className="my-5">{t('userPanel.sessionsNew.reflection.title')}</h2>

			<div className="mb-4">
				<Trans
					i18nKey="userPanel.sessionsNew.reflection.introText.before"
					components={{
						link: <LinkDocArticleWithIconWrapper articleLabel="reflection" />,
					}}
				/>

				<h2 className="mt-5">
					<Icon name="clock" iconStyle="regular" className="text-semirose mr-3" />
					{t('userPanel.sessionsNew.reflection.introText.primaryList.header')}
				</h2>
				<MessageBox variant="pink">
					<ul className="list arrow-list">
						<li>{t('userPanel.sessionsNew.reflection.introText.primaryList.el1')},</li>
						<li>{t('userPanel.sessionsNew.reflection.introText.primaryList.el2')},</li>
						<li>{t('userPanel.sessionsNew.reflection.introText.primaryList.el3')}</li>
					</ul>
				</MessageBox>

				{/* <h2 className="ty-5">{t('userPanel.sessionsNew.reflection.introText.secondaryList.header')}</h2>
				<ol>
					<li>{t('userPanel.sessionsNew.reflection.introText.secondaryList.el1')},</li>
					<li>{t('userPanel.sessionsNew.reflection.introText.secondaryList.el2')},</li>
					<li>{t('userPanel.sessionsNew.reflection.introText.secondaryList.el3')}</li>
				</ol> */}

				{/* <h2>{t('userPanel.sessionsNew.reflection.introText.after')}:</h2> */}
			</div>

			<div className="flex flex-row gap-4 flex-wrap justify-content-between align-items-center">
				<h2 className="ty-5">{t('userPanel.sessionsNew.reflection.introText.secondaryList.header')}:</h2>
				<div style={{ width: 200 }}>
					<ViewTypeDropdown
						defaultProfessional={hasProfessionalAnswers}
						onChange={(nextViewType) => setViewType(nextViewType)}
					/>
				</div>
			</div>

			<MessageBox
				icon="eye"
				title={t('userPanel.sessionsNew.reflection.infoVisibleOnlyForYou')}
				variant="rose"
				className="mb-4"
			/>

			{viewType === 'simpleView' && (
				<Box variant="white-bordered">
					{Array.from(simpleViewMap).map(([key, title]) => (
						<Controller
							name={key}
							control={control}
							render={({ field: { value, ...restField } }) => {
								return (
									<BoxSection
										header={title}
										headerClassName="flex flex-row gap-2 flex-wrap justify-items-start pt-2 font-semibold heading-16-24 text-on-blue-grey"
										contentClassName="pb-2"
									>
										<InputTextArea
											rows={3}
											{...restField}
											value={value}
											defaultValue={value}
											onBlur={() => {
												autosave(key);
											}}
										/>
										<MessageBox
											ref={(el: MessageBoxRef) => {
												if (el) {
													autosaveMessageRefs.current[key] = el;
												}
											}}
											message={t('userPanel.sessionsNew.reflection.autosaveMessage')}
											variant="success"
											autoHide
											autoHideDuration={3000}
											className="mb-2"
											tiny
										/>
									</BoxSection>
								);
							}}
						/>
					))}
				</Box>
			)}

			{viewType === 'professionalView' && (
				<>
					{sections.map((section) => (
						<div key={section.title} className="flex flex-column gap-3 mb-4">
							<h3 className="text-primary text-lg ml-2 mt-2">{section.title}</h3>
							<Box variant="white-bordered">
								{section.items.map(({ key, title }) => {
									return (
										<Controller
											name={key as ReflectionType}
											control={control}
											render={({ field: { value, ...restField } }) => (
												<BoxSection
													header={title}
													headerClassName="flex flex-row gap-2 flex-wrap justify-items-start pt-2 font-semibold heading-16-24 text-on-blue-grey"
													contentClassName="pb-2"
												>
													<InputTextArea
														rows={3}
														{...restField}
														value={value}
														defaultValue={value}
														onBlur={() => {
															autosave(key as ReflectionType);
														}}
													/>
													<MessageBox
														ref={(el: MessageBoxRef) => {
															if (el) {
																autosaveMessageRefs.current[key] = el;
															}
														}}
														message={t('userPanel.sessionsNew.reflection.autosaveMessage')}
														variant="success"
														autoHide
														autoHideDuration={3000}
														className="mb-2"
														tiny
													/>
												</BoxSection>
											)}
										/>
									);
								})}
							</Box>
						</div>
					))}
				</>
			)}

			<div className="flex flex-row justify-content-end mt-4">
				<Button submit label={t('actions.save')} loading={loading} icon="save" />
			</div>
		</form>
	);
};
